import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactPaginate from 'react-paginate';
import LabeledIcon from '../LabeledIcon';
import Paragraph from '../Paragraph';
import styles from './styles.scss';

let tableHeaderContainsMultiLine = false;

export default class Table extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    pageSize: PropTypes.number,
    pagination: PropTypes.shape({
      backText: PropTypes.node,
      nextText: PropTypes.node,
    }),
    onChangePage: PropTypes.func,
    dataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.node.isRequired,
        align: PropTypes.string,
        className: PropTypes.string,
        mobile: PropTypes.shape({
          order: PropTypes.number,
          className: PropTypes.string,
          hide: PropTypes.bool,
        }).isRequired,
      }).isRequired,
    ).isRequired,
    noDataLabel: PropTypes.node,
    striped: PropTypes.bool,
    startPage: PropTypes.number,
  };

  static defaultProps = {
    className: '',
    pageSize: 10,
    pagination: {
      nextText: 'Weiter',
      backText: 'Zurück',
    },
    noDataLabel: 'No Data',
    onChangePage: () => {},
    striped: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      page: props.startPage || 1,
    };
  }

  getDataForPage() {
    const { pageSize, dataSource } = this.props;
    const { page } = this.state;

    if (dataSource.length <= pageSize) {
      return dataSource;
    }

    const start = (page - 1) * pageSize;
    const end = page * pageSize;

    return dataSource.slice(start, end);
  }

  changePage = (data) => {
    const { selected } = data;

    this.setState({ page: selected + 1 });
    this.props.onChangePage(selected + 1);
  };

  renderHeadCells({ align, className, title, dataIndex }) {
    return (
      <th
        key={dataIndex}
        className={classNames(className, {
          [styles[`text-${align}`]]: align,
          [styles['text-left']]: !align,
          [styles.alignTop]: tableHeaderContainsMultiLine,
          [styles.alignBottom]: !tableHeaderContainsMultiLine,
        })}
      >
        {title}
      </th>
    );
  }

  renderHead() {
    const { columns } = this.props;

    tableHeaderContainsMultiLine = columns.some(({ title }) => Array.isArray(title));

    return (
      <thead>
        <tr>{columns.map((item) => this.renderHeadCells(item))}</tr>
      </thead>
    );
  }

  renderBodyCells(data, { align, className, dataIndex }, index) {
    const key = Array.isArray(dataIndex) ? dataIndex.join('-') : dataIndex;
    const td = (
      <td
        key={`row-${index}-${key}`}
        className={classNames(className, {
          [styles[`text-${align}`]]: align,
        })}
      />
    );

    if (!Array.isArray(dataIndex)) {
      return React.cloneElement(td, [], data[dataIndex]);
    }

    const html = dataIndex.reduce((acc, index) => {
      if (acc === '') return data[index];

      return `${acc}<br />${data[index] ? data[index] : ''}`;
    }, '');

    return React.cloneElement(td, {
      dangerouslySetInnerHTML: { __html: html },
    });
  }

  renderBody() {
    const { columns } = this.props;
    const data = this.getDataForPage();

    return (
      <tbody>
        {data.map((data, index) => (
          <tr key={`row-${index}`} className={styles.row}>
            {columns.map((column) => this.renderBodyCells(data, column, index))}
          </tr>
        ))}
      </tbody>
    );
  }

  renderNodataLabel() {
    return <div className={styles.noDataLabel}>{this.props.noDataLabel}</div>;
  }

  renderPagination() {
    const {
      dataSource,
      pageSize,
      pagination: { backText, nextText },
    } = this.props;

    if (!dataSource.length) return this.renderNodataLabel();

    const totalPagesCount = Math.ceil(dataSource.length / pageSize);

    if (totalPagesCount <= 1) {
      return <div />;
    }

    return (
      <div className={styles.pagination}>
        <ReactPaginate
          previousLabel={<LabeledIcon label={backText} prefixIcon="chevron-left" />}
          nextLabel={<LabeledIcon label={nextText} suffixIcon="chevron-right" />}
          containerClassName={styles.paginationList}
          previousClassName={styles.paginationBackLink}
          nextClassName={styles.paginationNextLink}
          disabledClassName={styles.disabled}
          pageCount={totalPagesCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          pageClassName={styles.page}
          onPageChange={this.changePage}
          activeLinkClassName={styles.activeItem}
        />
      </div>
    );
  }

  renderMobile() {
    const { columns } = this.props;
    const columnsMobile = Array.from(columns)
      .filter((col) => !col.mobile.hide)
      .sort((a, b) => a.mobile.order - b.mobile.order);
    const data = this.getDataForPage();

    return data.map((data, index) => (
      <div key={index} className={`row ${styles.mobileRow}`}>
        {columnsMobile.map(({ title, align, dataIndex, mobile }) => {
          return (
            <div
              key={`row-${index}-${dataIndex}`}
              className={classNames(mobile.className, {
                [styles[`text-${mobile.align}`]]: mobile.align,
                [styles[`text-${align}`]]: !mobile.align && align,
              })}
            >
              {data[dataIndex] ? (
                <>
                  <Paragraph>{title}</Paragraph>
                  {data[dataIndex]}
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    ));
  }

  render() {
    const { className, striped } = this.props;

    return (
      <div className={className}>
        <table
          className={classNames(styles.table, styles.tableResponsive, styles.hiddenXs, {
            [styles.striped]: striped,
          })}
        >
          {this.renderHead()}
          {this.renderBody()}
        </table>
        <div className={`${styles.tableMobile} ${styles.visibleXs}`}>{this.renderMobile()}</div>
        {this.renderPagination()}
      </div>
    );
  }
}
